import { Box, Flex, Progress } from '@chakra-ui/react';
import moment from 'moment';

import { Task, TaskStatus } from '@app/api/tasks';
import { Txt } from '@app/components/common';
import { TableColumn } from '@app/components/common/table';

import {
  formatProgress,
  formatTaskType,
  TASK_STATUS_COLORS,
  TASK_STATUS_LABELS,
  TASK_STATUS_OPTIONS,
  TASK_TYPE_OPTIONS,
} from './constants';
import { decodeUtf8Message } from './utils';

const renderDate = (value?: string) =>
  value ? (
    <Txt color="gray.500" whiteSpace="nowrap">
      {moment(value).format('DD.MM.YYYY HH:mm:ss')}
    </Txt>
  ) : (
    <Txt color="gray.400">—</Txt>
  );

export const useColumns = (): TableColumn<Task>[] => [
  {
    name: 'id',
    label: 'ID',
    options: {
      filter: false,
      sort: false,
    },
  },
  {
    name: 'type',
    label: 'Тип',
    options: {
      filter: {
        type: 'select',
        name: 'type',
        label: 'Тип задачи',
        onOptionsFetch: () => TASK_TYPE_OPTIONS,
      },
      sort: false,
      customBodyRender: (type: string) => <Txt>{formatTaskType(type)}</Txt>,
    },
  },
  {
    name: 'status',
    label: 'Статус',
    options: {
      filter: {
        type: 'select',
        name: 'status',
        label: 'Статус',
        onOptionsFetch: () => TASK_STATUS_OPTIONS,
      },
      sort: false,
      customBodyRender: (status: TaskStatus) => (
        <Txt color={TASK_STATUS_COLORS[status] || 'gray.500'} fontWeight="medium">
          {TASK_STATUS_LABELS[status] || status}
        </Txt>
      ),
    },
  },
  {
    name: 'progress',
    label: 'Прогресс',
    options: {
      filter: false,
      sort: false,
      customBodyRender: (progress: number, { rowData }) => (
        <Flex direction="column" gap={1} minW="120px">
          <Progress value={(progress || 0) * 100} size="sm" borderRadius="md" colorScheme="blue" />
          <Txt color="gray.500" fontSize="sm">
            {formatProgress(progress)}
            {rowData.total != null ? ` · ${rowData.processed || 0}/${rowData.total}` : ''}
          </Txt>
        </Flex>
      ),
    },
  },
  {
    name: 'failed',
    label: 'Ошибки',
    options: {
      filter: false,
      sort: false,
      customBodyRender: (failed?: number) =>
        failed ? <Txt color="red.500">{failed}</Txt> : <Txt color="gray.400">0</Txt>,
    },
  },
  {
    name: 'source',
    label: 'Источник',
    options: {
      filter: false,
      sort: false,
      customBodyRender: (source?: string) => (source ? <Txt>{source}</Txt> : <Txt color="gray.400">—</Txt>),
    },
  },
  {
    name: 'created_by',
    label: 'Автор',
    options: {
      filter: false,
      sort: false,
      customBodyRender: (createdBy?: string) =>
        createdBy ? <Txt>{createdBy}</Txt> : <Txt color="gray.400">—</Txt>,
    },
  },
  {
    name: 'created_at',
    label: 'Создана',
    options: {
      filter: false,
      sort: false,
      customBodyRender: renderDate,
    },
  },
  {
    name: 'finished_at',
    label: 'Завершена',
    options: {
      filter: false,
      sort: false,
      customBodyRender: renderDate,
    },
  },
  {
    name: 'error',
    label: 'Ошибка',
    options: {
      filter: false,
      sort: false,
      customBodyRender: (error?: string) =>
        error ? (
          <Box maxW="240px" whiteSpace="pre-wrap" wordBreak="break-word">
            <Txt color="red.500" noOfLines={3}>
              {decodeUtf8Message(error)}
            </Txt>
          </Box>
        ) : (
          <Txt color="gray.400">—</Txt>
        ),
    },
  },
];
